import { Dayjs } from "dayjs";
import RepositoryBase from "./RepositoryBase";
import { ResponseEnvelope } from "./RepositoryBase";
import GetCountPeriodResponse from "./CountPeriodResponses/GetCountPeriodResponse";
import GetAllCountPeriodsResponse from "./CountPeriodResponses/GetAllCountPeriodsResponse";
import AddCountPeriodResponse from "./CountPeriodResponses/AddCountPeriodResponse";
import UpdateCountPeriodResponse from "./CountPeriodResponses/UpdateCountPeriodResponse";
import DeleteCountPeriodResponse from "./CountPeriodResponses/DeleteCountPeriodResponse";
import UpdateCountPeriod from "./CountPeriodDataModels/UpdateCountPeriod";
import GetNotCountedWorkersResponse from "./CountPeriodResponses/GetNotCountedWorkersResponse";
import AddCountedWorkerResponse from "./CountPeriodResponses/AddCountedWorkerResponse";
import RemoveCountedWorkerResponse from "./CountPeriodResponses/RemoveCountedWorkerResponse";
import GetCountPeriodWorkerCountEventsResponse from "./CountPeriodResponses/GetCountPeriodWorkerCountEventsResponse";

export default class CountPeriodRepository extends RepositoryBase {

    public static async getCountPeriodById(locationId: string, countPeriodId: string): Promise<ResponseEnvelope<GetCountPeriodResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<GetCountPeriodResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/countperiod/${countPeriodId}`, {
                method: "GET",
                mode: "cors",
                credentials: "include",
                headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<GetCountPeriodResponse>(
                true,
                data as GetCountPeriodResponse
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async getAllCountPeriods(locationId: string, createdBy?: string, type?: number, startDate?: Dayjs, endDate?: Dayjs): Promise<ResponseEnvelope<GetAllCountPeriodsResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<GetAllCountPeriodsResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            const params = new URLSearchParams();
            if (createdBy) params.set("createdBy", createdBy);
            if (type !== undefined) params.set("type", type.toString());
            if (startDate) params.set("startDate", startDate.format("YYYY-MM-DD"));
            if (endDate) params.set("endDate", endDate.format("YYYY-MM-DD"));

            const query = params.toString();
            const url = `${this.protocol}://${this.ip}:${this.port}/api/${locationId}/countperiod${query ? `?${query}` : ""}`;

            var response = await fetch(url, {
                method: "GET",
                mode: "cors",
                credentials: "include",
                headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<GetAllCountPeriodsResponse>(
                true,
                data as GetAllCountPeriodsResponse
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async getNotCountedWorkers(locationId: string, countPeriodId: string): Promise<ResponseEnvelope<GetNotCountedWorkersResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<GetNotCountedWorkersResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/countperiod/${countPeriodId}/notcountedworkers`, {
                method: "GET",
                mode: "cors",
                credentials: "include",
                headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<GetNotCountedWorkersResponse>(
                true,
                data as GetNotCountedWorkersResponse
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async getCountPeriodWorkerCountEvents(locationId: string, countPeriodId: string): Promise<ResponseEnvelope<GetCountPeriodWorkerCountEventsResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<GetCountPeriodWorkerCountEventsResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/countperiod/${countPeriodId}/countedevents`, {
                method: "GET",
                mode: "cors",
                credentials: "include",
                headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<GetCountPeriodWorkerCountEventsResponse>(
                true,
                {
                    success: data.success,
                    events: (data.events ?? []).map((e: any) => ({
                        id: e.id,
                        countPeriodId: e.countPeriodId,
                        workerId: e.workerId,
                        action: e.action,
                        updatedBy: e.updatedBy,
                        updatedByName: e.updatedByName,
                        updatedOn: e.updatedOn,
                        debtDelta: typeof e.debtDelta === "number" ? e.debtDelta : 0,
                    })),
                }
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async addCountPeriod(locationId: string, transactionId: string|null, type: number, startDate: Dayjs, endDate: Dayjs): Promise<ResponseEnvelope<AddCountPeriodResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<AddCountPeriodResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/countperiod`, {
                method: "POST",
                mode: "cors",
                credentials: "include",
                headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
                body: JSON.stringify({
                    transactionId: transactionId,
                    type: type,
                    startDate: startDate.format("YYYY-MM-DD"),
                    endDate: endDate.format("YYYY-MM-DD")
                })
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<AddCountPeriodResponse>(
                true,
                data as AddCountPeriodResponse
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async updateCountPeriod(locationId: string, countPeriodId: string, update: UpdateCountPeriod): Promise<ResponseEnvelope<UpdateCountPeriodResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<UpdateCountPeriodResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/countperiod/${countPeriodId}`, {
                method: "PUT",
                mode: "cors",
                credentials: "include",
                headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
                body: JSON.stringify({
                    locationId: update.locationId ?? null,
                    transactionId: update.transactionId ?? null,
                    type: update.type ?? null,
                    startDate: update.startDate?.format("YYYY-MM-DD") ?? null,
                    endDate: update.endDate?.format("YYYY-MM-DD") ?? null,
                    isDeleted: update.isDeleted ?? null
                })
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<UpdateCountPeriodResponse>(
                true,
                data as UpdateCountPeriodResponse
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async addCountedWorker(locationId: string, countPeriodId: string, workerId: string, debtDelta: number = 0): Promise<ResponseEnvelope<AddCountedWorkerResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<AddCountedWorkerResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/countperiod/${countPeriodId}/countedworker`, {
                method: "PUT",
                mode: "cors",
                credentials: "include",
                headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
                body: JSON.stringify({
                    workerId: workerId,
                    debtDelta: debtDelta
                })
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<AddCountedWorkerResponse>(
                true,
                data as AddCountedWorkerResponse
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async removeCountedWorker(locationId: string, countPeriodId: string, workerId: string, debtDelta: number = 0): Promise<ResponseEnvelope<RemoveCountedWorkerResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<RemoveCountedWorkerResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            // debtDelta records the debt reverted by unchecking, as query param
            // because the DELETE carries no body.
            const params = new URLSearchParams({ debtDelta: debtDelta.toString() });

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/countperiod/${countPeriodId}/countedworker/${workerId}?${params.toString()}`, {
                method: "DELETE",
                mode: "cors",
                credentials: "include",
                headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<RemoveCountedWorkerResponse>(
                true,
                data as RemoveCountedWorkerResponse
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async deleteCountPeriod(locationId: string, countPeriodId: string): Promise<ResponseEnvelope<DeleteCountPeriodResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<DeleteCountPeriodResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/countperiod/${countPeriodId}`, {
                method: "DELETE",
                mode: "cors",
                credentials: "include",
                headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<DeleteCountPeriodResponse>(
                true,
                data as DeleteCountPeriodResponse
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }
}
